import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { format } from 'date-fns';
import { 
  Puzzle, 
  Globe,
  CreditCard,
  Lock,
  Calculator,
  MessageSquare,
  Zap,
  Building2,
  Plus,
  Loader2,
  RefreshCw,
  Search,
  CheckCircle2,
  XCircle,
  Play,
  Pause,
  Settings,
  ExternalLink,
  Activity,
  AlertTriangle,
  Workflow,
  Store,
  Power,
  Trash2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';

export default function IntegrationsPage() {
  const { currentProperty } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [marketplaceIntegrations, setMarketplaceIntegrations] = useState([]);
  const [configuredIntegrations, setConfiguredIntegrations] = useState([]);
  const [automationRules, setAutomationRules] = useState([]);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  
  // Form state
  const [setupForm, setSetupForm] = useState({
    name: '',
    credentials: {},
    config: {},
    sync_frequency: 'HOURLY'
  });

  useEffect(() => {
    if (currentProperty?.id) {
      loadDashboard();
      loadCategories();
      loadMarketplace();
    }
  }, [currentProperty]);

  useEffect(() => {
    if (currentProperty?.id && activeTab === 'configured') {
      loadConfiguredIntegrations();
    } else if (currentProperty?.id && activeTab === 'automation') {
      loadAutomationRules();
    }
  }, [currentProperty, activeTab]);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/integrations/dashboard?property_id=${currentProperty.id}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/integrations/marketplace/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadMarketplace = async () => {
    try {
      let url = '/integrations/marketplace';
      if (selectedCategory && selectedCategory !== 'all') {
        url += `?category_id=${selectedCategory}`;
      }
      const response = await api.get(url);
      setMarketplaceIntegrations(response.data || []);
    } catch (error) {
      console.error('Error loading marketplace:', error);
    }
  };

  const loadConfiguredIntegrations = async () => {
    try {
      const response = await api.get(`/integrations/configured?property_id=${currentProperty.id}`);
      setConfiguredIntegrations(response.data || []);
    } catch (error) {
      console.error('Error loading configured integrations:', error);
    }
  };

  const loadAutomationRules = async () => {
    try {
      const response = await api.get(`/integrations/automation?property_id=${currentProperty.id}`);
      setAutomationRules(response.data || []);
    } catch (error) {
      console.error('Error loading automation rules:', error);
    }
  };

  const handleSetupIntegration = async () => {
    try {
      await api.post(`/integrations/setup?property_id=${currentProperty.id}`, {
        integration_definition_id: selectedIntegration.id,
        name: setupForm.name || selectedIntegration.name,
        credentials: setupForm.credentials,
        config: setupForm.config,
        sync_frequency: setupForm.sync_frequency
      });
      toast.success('Integration configured successfully');
      setShowSetupDialog(false);
      loadConfiguredIntegrations();
      loadDashboard();
      resetSetupForm();
    } catch (error) {
      toast.error('Failed to configure integration');
    }
  };

  const handleActivateIntegration = async (integrationId) => {
    try {
      await api.post(`/integrations/configured/${integrationId}/activate`);
      toast.success('Integration activated');
      loadConfiguredIntegrations();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to activate integration');
    }
  };

  const handleDeactivateIntegration = async (integrationId) => {
    try {
      await api.post(`/integrations/configured/${integrationId}/deactivate`);
      toast.success('Integration deactivated');
      loadConfiguredIntegrations();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to deactivate integration');
    }
  };

  const handleTriggerSync = async (integrationId) => {
    try {
      await api.post(`/integrations/configured/${integrationId}/sync`);
      toast.success('Sync triggered successfully');
      loadConfiguredIntegrations();
    } catch (error) {
      toast.error('Failed to trigger sync');
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    try {
      await api.delete(`/integrations/configured/${integrationId}`);
      toast.success('Integration removed');
      loadConfiguredIntegrations();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to remove integration');
    }
  };

  const resetSetupForm = () => {
    setSetupForm({
      name: '',
      credentials: {},
      config: {},
      sync_frequency: 'HOURLY'
    });
    setSelectedIntegration(null);
  };

  const openSetupDialog = (integration) => {
    setSelectedIntegration(integration);
    setSetupForm({
      name: integration.name,
      credentials: {},
      config: {},
      sync_frequency: 'HOURLY'
    });
    setShowSetupDialog(true);
  };

  const getCategoryIcon = (iconName) => {
    const icons = {
      'globe': Globe,
      'credit-card': CreditCard,
      'lock': Lock,
      'calculator': Calculator,
      'message-square': MessageSquare,
      'zap': Zap
    };
    return icons[iconName] || Puzzle;
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'ACTIVE': return 'default';
      case 'ERROR': return 'destructive';
      case 'INACTIVE': case 'PAUSED': return 'secondary';
      default: return 'outline';
    }
  };

  const filteredIntegrations = marketplaceIntegrations.filter(i => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return i.name?.toLowerCase().includes(query) ||
           i.provider?.toLowerCase().includes(query) ||
           i.short_description?.toLowerCase().includes(query);
  });

  if (!currentProperty) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="integrations-no-property">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">No Property Selected</h3>
          <p className="text-muted-foreground">Please select a property</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="integrations-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground">Connect with third-party services and automate workflows</p>
        </div>
        <Button onClick={loadDashboard} variant="outline" data-testid="refresh-integrations-btn">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-int-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="marketplace" data-testid="tab-marketplace">Marketplace</TabsTrigger>
          <TabsTrigger value="configured" data-testid="tab-configured">Configured</TabsTrigger>
          <TabsTrigger value="automation" data-testid="tab-automation">Automation</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card data-testid="stat-configured">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Configured</CardTitle>
                <Puzzle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.configured?.total || 0}</div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-green-500">{dashboardData?.configured?.active || 0} active</span>
                  {dashboardData?.configured?.error > 0 && (
                    <span className="text-red-500">{dashboardData?.configured?.error} error</span>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-syncs">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Recent Syncs</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.syncs?.recent_total || 0}</div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-green-500">{dashboardData?.syncs?.successful || 0} successful</span>
                  <span className="text-red-500">{dashboardData?.syncs?.failed || 0} failed</span>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-automation">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Automation Rules</CardTitle>
                <Workflow className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.automation?.total_rules || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboardData?.automation?.active_rules || 0} active
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-available">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Available</CardTitle>
                <Store className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.marketplace?.available || 0}</div>
                <p className="text-xs text-muted-foreground">in marketplace</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => setActiveTab('marketplace')}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <Store className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Browse Marketplace</h3>
                    <p className="text-sm text-muted-foreground">Discover integrations</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => setActiveTab('configured')}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <Settings className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Manage Integrations</h3>
                    <p className="text-sm text-muted-foreground">Configure and monitor</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => setActiveTab('automation')}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                    <Workflow className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Automation</h3>
                    <p className="text-sm text-muted-foreground">Create workflows</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Marketplace Tab */}
        <TabsContent value="marketplace" className="space-y-6">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search integrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-integrations"
              />
            </div>
            <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); loadMarketplace(); }}>
              <SelectTrigger className="w-[200px]" data-testid="filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Integration Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredIntegrations.map((integration) => {
              const CategoryIcon = getCategoryIcon(integration.integration_categories?.icon);
              return (
                <Card key={integration.id} className="relative" data-testid={`integration-${integration.code}`}>
                  {integration.is_featured && (
                    <Badge className="absolute top-2 right-2" variant="secondary">Featured</Badge>
                  )}
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <CategoryIcon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{integration.name}</h3>
                        <p className="text-sm text-muted-foreground">{integration.provider}</p>
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-muted-foreground line-clamp-2">
                      {integration.short_description}
                    </p>
                    {integration.features && (
                      <div className="mt-4 flex flex-wrap gap-1">
                        {integration.features.slice(0, 3).map((feature, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{feature}</Badge>
                        ))}
                      </div>
                    )}
                    <div className="mt-4 flex justify-between items-center">
                      <Badge variant="outline">{integration.integration_categories?.name}</Badge>
                      <Button size="sm" onClick={() => openSetupDialog(integration)}>
                        <Plus className="h-4 w-4 mr-1" /> Add
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {filteredIntegrations.length === 0 && (
              <div className="col-span-3 text-center py-8 text-muted-foreground">
                No integrations found
              </div>
            )}
          </div>
        </TabsContent>

        {/* Configured Tab */}
        <TabsContent value="configured" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Configured Integrations</CardTitle>
              <CardDescription>Manage your connected services</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Integration</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Sync Frequency</TableHead>
                    <TableHead>Last Sync</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configuredIntegrations.map((integration) => (
                    <TableRow key={integration.id} data-testid={`configured-${integration.id}`}>
                      <TableCell>
                        <div className="font-medium">{integration.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {integration.integration_definitions?.code}
                        </div>
                      </TableCell>
                      <TableCell>{integration.integration_definitions?.provider}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{integration.sync_frequency}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {integration.last_sync_at 
                          ? format(new Date(integration.last_sync_at), 'MMM d, HH:mm')
                          : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(integration.status)}>
                          {integration.status === 'ACTIVE' ? (
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" /> Active
                            </span>
                          ) : integration.status === 'ERROR' ? (
                            <span className="flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" /> Error
                            </span>
                          ) : (
                            integration.status
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {integration.status === 'ACTIVE' ? (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleTriggerSync(integration.id)}
                                title="Sync now"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeactivateIntegration(integration.id)}
                                title="Deactivate"
                              >
                                <Pause className="h-4 w-4 text-orange-500" />
                              </Button>
                            </>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleActivateIntegration(integration.id)}
                              title="Activate"
                            >
                              <Power className="h-4 w-4 text-green-500" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteIntegration(integration.id)}
                            title="Remove"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {configuredIntegrations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No integrations configured. Browse the marketplace to add one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automation Tab */}
        <TabsContent value="automation" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Automation Rules</CardTitle>
                  <CardDescription>Automate workflows and processes</CardDescription>
                </div>
                <Button data-testid="add-automation-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Rule
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rule Name</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Actions</TableHead>
                    <TableHead>Executions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {automationRules.map((rule) => (
                    <TableRow key={rule.id} data-testid={`automation-rule-${rule.id}`}>
                      <TableCell>
                        <div className="font-medium">{rule.name}</div>
                        {rule.description && (
                          <div className="text-sm text-muted-foreground">{rule.description}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{rule.trigger_type}</Badge>
                      </TableCell>
                      <TableCell>{rule.actions?.length || 0} actions</TableCell>
                      <TableCell>{rule.trigger_count || 0}</TableCell>
                      <TableCell>
                        <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">
                          <Settings className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {automationRules.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No automation rules configured
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configure Integration</DialogTitle>
            <DialogDescription>
              Set up {selectedIntegration?.name} for your property
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Display Name *</Label>
              <Input
                value={setupForm.name}
                onChange={(e) => setSetupForm({...setupForm, name: e.target.value})}
                placeholder={selectedIntegration?.name}
                data-testid="input-integration-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Sync Frequency</Label>
              <Select value={setupForm.sync_frequency} onValueChange={(v) => setSetupForm({...setupForm, sync_frequency: v})}>
                <SelectTrigger data-testid="select-sync-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="REALTIME">Real-time</SelectItem>
                  <SelectItem value="EVERY_5_MIN">Every 5 minutes</SelectItem>
                  <SelectItem value="EVERY_15_MIN">Every 15 minutes</SelectItem>
                  <SelectItem value="HOURLY">Hourly</SelectItem>
                  <SelectItem value="DAILY">Daily</SelectItem>
                  <SelectItem value="MANUAL">Manual only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                You can configure credentials and detailed settings after setup.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>Cancel</Button>
            <Button onClick={handleSetupIntegration} data-testid="submit-setup-btn">Configure</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
